import { styled } from 'styled-components';
import { getPalette } from '../../../utils/visualisations/visPalettes';

/* The for/against underlines distinguish the team from its opponent, the same
   distinction the visualisations draw, so they read the chart palette rather
   than a theme token. See ADR-0002. */
const forAgainstColour = (theme, side) =>
  getPalette(theme.isDark).forAgainst[side].main;

export const StyledBoxScoreTable = styled.table`
  width: 100%;
  min-height: 40px;
  border-spacing: 0;
  border-radius: ${({ theme }) => theme.borderRadius.secondary};
  font-size: ${({ theme }) => theme.typography.headingSmall.fontSize};

  thead {
    tr {
      background-color: ${({ theme }) => theme.colours.canvas.tertiary.main};
    }
  }

  th {
    min-width: 20px;
    padding: ${({ theme }) => theme.sizing.regular}
      ${({ theme }) => theme.sizing.small};
    font-size: ${({ theme }) => theme.typography.bodyRegular.fontSize};
    color: ${({ theme }) => theme.colours.ink.primary.main};

    &.long-name {
      min-width: 100px;
      border-left: 0;
    }

    &.for {
      background-color: initial;
      border-bottom: 3px solid ${({ theme }) => forAgainstColour(theme, 'for')};
      color: ${({ theme }) => theme.colours.ink.primary.main};
    }

    &.against {
      background-color: initial;
      border-bottom: 3px solid
        ${({ theme }) => forAgainstColour(theme, 'against')};
      color: ${({ theme }) => theme.colours.ink.primary.main};
    }

    &:first-child {
      border-radius: ${({ theme }) => `${theme.borderRadius.secondary} 0 0 0`};
    }

    &:last-child {
      border-radius: 0;
    }

    &.space {
      min-width: ${({ theme }) => theme.sizing.tiny};
      height: 2px;
      border: none;
      font-size: 0px;
      padding: 0px ${({ theme }) => theme.sizing.xtiny}
        ${({ theme }) => theme.sizing.tiny} ${({ theme }) => theme.sizing.xtiny};
      background-color: ${({ theme }) => theme.colours.canvas.tertiary.main};
    }
  }

  tbody {
    tr:nth-child(odd) {
      background-color: ${({ theme }) => theme.colours.canvas.secondary.main};
    }

    td.space,
    tr:nth-child(even) td.space {
      min-width: ${({ theme }) => theme.sizing.tiny};
      height: 2px;
      background-color: ${({ theme }) => theme.colours.canvas.tertiary.main};
      border: none;
      font-size: 0px;
      padding: 0px ${({ theme }) => theme.sizing.xtiny}
        ${({ theme }) => theme.sizing.tiny} ${({ theme }) => theme.sizing.xtiny};
    }

    td {
      padding: ${({ theme }) => theme.sizing.xtiny}
        ${({ theme }) => theme.sizing.tiny};
      text-align: right;
      border-right: 1px solid
        ${({ theme }) => theme.colours.canvas.primary.main};
      border-bottom: 1px solid
        ${({ theme }) => theme.colours.canvas.primary.main};
    }

    tr td:first-child {
      border-left: 1px solid ${({ theme }) => theme.colours.canvas.primary.main};
      text-align: left;
    }

    tr:last-child td.space {
      padding-bottom: ${({ theme }) => theme.sizing.small};
    }

    tr:last-child td:first-child {
      border-radius: ${({ theme }) => `0 0 0 ${theme.borderRadius.secondary}`};
    }

    tr:last-child td:last-child {
      border-radius: ${({ theme }) => `0 0 ${theme.borderRadius.secondary} 0`};
    }

    .metric-name,
    tr td.metric-name:first-child {
      text-align: right;
      border-left: none;
    }

    tr:hover td,
    tr:hover td.space {
      background-color: ${({ theme }) => theme.colours.canvas.tertiary.main};
      color: ${({ theme }) => theme.colours.brand.ink};
    }

    tr:nth-child(even) {
      td {
        &.space {
          background-color: ${({ theme }) =>
            theme.colours.canvas.tertiary.main};
        }
      }
    }

    tr {
      &:hover {
        td {
          background-color: ${({ theme }) =>
            theme.applyOpacity(theme.colours.interface.main, 'highlight')};
          color: ${({ theme }) => theme.colours.ink.primary.main};

          &.space {
            background-color: ${({ theme }) =>
              theme.colours.canvas.tertiary.main};
          }
        }
      }

      td {
        padding: ${({ theme }) => theme.sizing.small};
        border-top: 1px solid
          ${({ theme }) => theme.colours.canvas.primary.main};

        &.partition-cell {
          background-color: ${({ theme }) =>
            theme.colours.canvas.secondary.main};
          color: ${({ theme }) => theme.colours.ink.primary.main};
          font-weight: ${({ theme }) =>
            theme.typography.headingLarge.fontWeight};
          font-size: ${({ theme }) => theme.typography.bodyRegular.fontSize};
        }

        &.space {
          padding: ${({ theme }) => theme.sizing.small}
            ${({ theme }) => theme.sizing.small};

          &:first-child {
            border: 0;
          }
        }

        &.space + .space {
          border: 0;
        }

        &.space + td {
          border-left: 1px solid
            ${({ theme }) => theme.colours.canvas.primary.main};
        }
      }
    }
  }
`;
